import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { 
  Globe, 
  Layers, 
  Code, 
  Image, 
  CheckCircle, 
  Sparkles,
  Rocket,
  Palette,
  TestTube,
  ArrowRight,
  Star,
  Clock,
  Users,
  Target,
  Zap,
  Award,
  ExternalLink,
  Github
} from "lucide-react";
import { projectsData, startupData, appsData } from "../data/projectsData";
import { NavBar } from "./NavBar";

const ProjectDetails = () => {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    const allProjects = [...projectsData, ...startupData, ...appsData];
    const foundProject = allProjects.find(
      (proj) => proj.slug === slug || proj.id === slug
    );
    setProject(foundProject);
  }, [slug]);

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading project details...</p>
        </div>
      </div>
    );
  }

  const developmentSteps = [
    {
      icon: <Target className="w-6 h-6" />,
      title: "Planning & Strategy",
      description: "Analyzed project requirements and structured the app architecture.",
      color: "text-blue-500",
      bgColor: "bg-blue-50"
    },
    {
      icon: <Palette className="w-6 h-6" />,
      title: "UI/UX Design",
      description: "Created modern, responsive UI focusing on usability and clean design.",
      color: "text-purple-500",
      bgColor: "bg-purple-50"
    },
    {
      icon: <Code className="w-6 h-6" />,
      title: "Development",
      description: "Implemented core functionalities and backend logic using modern technologies.",
      color: "text-green-500",
      bgColor: "bg-green-50"
    },
    {
      icon: <Rocket className="w-6 h-6" />,
      title: "Testing & Deployment",
      description: "Tested across devices and deployed to a live production server.",
      color: "text-orange-500",
      bgColor: "bg-orange-50"
    }
  ];

  return (
    <>
      <NavBar />
      
      {/* Hero Section with Gradient Background */}
      <div className="relative project-hero pt-24">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center text-black">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{project.title}</h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">{project.description}</p>
          </div>
        </div>
        
        {/* Curved bottom edge */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="fill-current text-white" viewBox="0 0 1440 48">
            <path d="M0,48L1440,0L1440,48L0,48Z"></path>
          </svg>
        </div>
      </div>

      {/* Main Content */}
      <section className="bg-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Hero Image with Overlay Stats */}
          <div className="relative -mt-20 mb-12">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src={project.imgUrl || project.image}
                alt={project.title}
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              
              {/* Quick Stats */}
              <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                <div className="flex flex-wrap gap-8">
                  {project.category && (
                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                        <Layers className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-sm opacity-80">Category</p>
                        <p className="font-semibold">{project.category}</p>
                      </div>
                    </div>
                  )}
                  {project.technologies && (
                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                        <Code className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-sm opacity-80">Technologies</p>
                        <p className="font-semibold">{project.technologies.length}+</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-4 mb-12">
            {(project.url || project.website) && (
              <a
                href={project.url || project.website}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                <Globe className="mr-2" size={20} /> View Live Project
                <ExternalLink className="ml-2" size={16} />
              </a>
            )}
            {project.github && (
              <a
                href={project.github}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-all transform hover:scale-105 shadow-lg"
              >
                <Github className="mr-2" size={20} /> View Source Code
              </a>
            )}
          </div>

          {/* Navigation Tabs */}
          <div className="border-b border-gray-200 mb-8">
            <nav className="flex gap-8">
              {["overview", "features", "development", "screenshots"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`pb-4 px-1 font-medium capitalize transition-all relative ${
                    activeTab === tab
                      ? "text-blue-600 border-b-2 border-blue-600"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {tab}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="space-y-12">
            
            {/* Overview Tab */}
            {activeTab === "overview" && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-6">
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h2 className="flex items-center text-2xl font-bold text-gray-800 mb-4">
                      <Layers className="mr-3 text-blue-600" size={24} />
                      Project Overview
                    </h2>
                    <p className="text-gray-600 leading-relaxed">
                      {project.details || project.overview || "No additional details provided."}
                    </p>
                  </div>

                  {/* Special/Unique Feature */}
                  {project.specialFeature && (
                    <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-100">
                      <h2 className="flex items-center text-2xl font-bold text-gray-800 mb-4">
                        <Sparkles className="mr-3 text-purple-600" size={24} />
                        Special Feature
                      </h2>
                      <div className="flex flex-col md:flex-row gap-6">
                        {project.specialFeature.image && (
                          <img
                            src={project.specialFeature.image}
                            alt="Special feature"
                            className="w-full md:w-48 h-32 object-cover rounded-lg shadow-md"
                          />
                        )}
                        <p className="text-gray-600">{project.specialFeature.description}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Project Info Sidebar */}
                <div className="space-y-6">
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h3 className="font-semibold text-gray-800 mb-4">Project Details</h3>
                    <div className="space-y-4">
                      {project.client && (
                        <div className="flex items-start gap-3">
                          <Users className="w-5 h-5 text-blue-600 mt-1" />
                          <div>
                            <p className="text-sm text-gray-500">Client</p>
                            <p className="font-medium">{project.client}</p>
                          </div>
                        </div>
                      )}
                      {project.timeline && (
                        <div className="flex items-start gap-3">
                          <Clock className="w-5 h-5 text-green-600 mt-1" />
                          <div>
                            <p className="text-sm text-gray-500">Timeline</p>
                            <p className="font-medium">{project.timeline}</p>
                          </div>
                        </div>
                      )}
                      {project.role && (
                        <div className="flex items-start gap-3">
                          <Award className="w-5 h-5 text-orange-600 mt-1" />
                          <div>
                            <p className="text-sm text-gray-500">Role</p>
                            <p className="font-medium">{project.role}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Technologies */}
                  {project.technologies?.length > 0 && (
                    <div className="bg-gray-50 rounded-xl p-6">
                      <h3 className="font-semibold text-gray-800 mb-4">Technologies Used</h3>
                      <div className="flex flex-wrap gap-2">
                        {project.technologies.map((tech, index) => (
                          <span
                            key={index}
                            className="px-3 py-1.5 bg-white text-gray-700 rounded-lg text-sm font-medium shadow-sm border border-gray-200"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Features Tab */}
            {activeTab === "features" && project.features && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {project.features.map((feature, index) => (
                  <div
                    key={index}
                    className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                          <CheckCircle className="w-5 h-5 text-blue-600" />
                        </div>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-800 mb-2">{feature}</h3>
                        <p className="text-gray-600 text-sm">
                          Detailed implementation of {feature} to enhance user experience.
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Development Tab */}
            {activeTab === "development" && (
              <div className="space-y-8">
                <div className="relative">
                  <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                  <div className="space-y-8">
                    {developmentSteps.map((step, index) => (
                      <div key={index} className="relative flex items-start gap-6">
                        <div className={`relative z-10 flex-shrink-0 w-16 h-16 ${step.bgColor} rounded-xl flex items-center justify-center`}>
                          <div className={step.color}>{step.icon}</div>
                        </div>
                        <div className="flex-1 bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                          <h3 className="text-lg font-semibold text-gray-800 mb-2">{step.title}</h3>
                          <p className="text-gray-600">{step.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Screenshots Tab */}
            {activeTab === "screenshots" && project.additionalImages?.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {project.additionalImages.map((img, index) => (
                  <div key={index} className="group relative">
                    <div className="aspect-video overflow-hidden rounded-xl shadow-lg">
                      <img
                        src={img}
                        alt={`${project.title} screenshot ${index + 1}`}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-end p-4">
                      <p className="text-white font-medium">Screenshot {index + 1}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default ProjectDetails;